const get = require('lodash.get');

import {
    MessageSendErrorAction,
    MessageSentAction,
    MessagesMarkedReadAction,
    NewBotMessageAction,
    RealConvoStartedAction,
    RemoveConversationAction,
    SendMessageAction,
    SocketEventNewMessageAction,
    ConvoDetailsFetchedAction,
} from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { PreMessage, TranscriptMessage } from '../typedefs/api';
import { Message, MessageState as State } from '../typedefs/store';
import { createReducer } from './util';

const initialState: State = {
    messages: [],
};

const handlers = {
    [actionTypes.CONVO_DETAILS_FETCHED]: convoDetailsFetched,
    [actionTypes.SOCKET_EVENT_NEW_BOT_MESSAGE]: newBotMessage,
    [actionTypes.NEW_BOT_MESSAGE]: newBotMessage,
    [actionTypes.SOCKET_EVENT_NEW_MESSAGE]: newSocketMessage,
    [actionTypes.SEND_MESSAGE]: sendMessage,
    [actionTypes.MESSAGE_SENT]: messageSent,
    [actionTypes.MESSAGE_SEND_ERROR]: messageSendError,
    [actionTypes.MESSAGES_MARKED_READ]: messagesMarkedRead,
    [actionTypes.REMOVE_CONVERSATION]: removeConversation,
};

function newBotMessage(state: State, action: NewBotMessageAction): State {
    if (!action.payload.type) {
        action.payload.type = 'bot';
    }

    return {
        ...state,
        messages: state.messages.concat({
            id: action.payload.id,
            timestamp: action.payload.timestamp,
            text: action.payload.text,
            buttons: action.payload.buttons,
            fromType: action.payload.type,
            category: 'message',
            type: action.payload.type === 'bot' ? 'bot-to-visitor' : 'visitor-to-bot',
        }),
    };
}

function convoDetailsFetched(state: State, action: ConvoDetailsFetchedAction): State {
    return {
        ...state,
        messages: mapMessages(action.payload.messages),
    };
}

function mapMessages(messages: TranscriptMessage[]): Message[] {
    const hiddenPresenseMessageTypes = [
        'visitor-joined',
        'visitor-left',
        'agent-ended',
        'visitor-url',
        'visitor-video-request',
        'visitor-ended',
        'agent-ended',
    ];

    return messages
        .filter(
            message =>
                message.category === 'message' ||
                !hiddenPresenseMessageTypes.includes(message.type),
        )
        .map(message => {
            let fromType = 'bot';
            if (message.agentId) fromType = 'agent';
            if (message.visitorId) fromType = 'visitor';

            return {
                id: message.messageId,
                timestamp: message.timestamp,
                text: message.body,
                fromType,
                category: message.category,
                type: message.type,
                attachments: message.attachments,
                fromId: message.agentId || message.visitorId || undefined,
                readReceipts: message.readReceipts,
            };
        });
}

function newSocketMessage(state: State, action: SocketEventNewMessageAction): State {
    if (action.payload.fromType === 'visitor') return state;

    let message: Message = {
        id: action.payload.messageId,
        timestamp: action.payload.timestamp,
        text: action.payload.messageText || '',
        fromType: action.payload.fromType,
        fromId: action.payload.fromId,
        category: action.payload.category,
        type: action.payload.type,
    };

    if (action.payload.attachments) {
        try {
            let attachments = JSON.parse(action.payload.attachments);
            message.attachments = attachments;
        } catch (e) {
            // ignore
        }
    }

    return {
        ...state,
        messages: state.messages.concat(message),
    };
}

function sendMessage(state: State, action: SendMessageAction): State {
    // handle resends
    if (hasExistingMessage(state, action.payload.tempId)) {
        return handleMessageResend(state, action);
    }

    // regular sends
    return {
        ...state,
        messages: state.messages.concat({
            id: action.payload.tempId,
            timestamp: new Date().toISOString(),
            text: action.payload.text,
            fromType: 'visitor',
            category: 'message',
            type: 'visitor-to-agent',
            sending: true,
        }),
    };
}

function handleMessageResend(state: State, action: SendMessageAction) {
    return {
        ...state,
        messages: state.messages.map(message => {
            if (message.id === action.payload.tempId) {
                return {
                    ...message,
                    timestamp: new Date().toISOString(),
                    sending: true,
                };
            }
            return message;
        }),
    };
}

function hasExistingMessage(state: State, id: string | number): boolean {
    const existingMessage = state.messages.filter(message => message.id === id);
    return existingMessage.length > 0;
}

function messageSent(state: State, action: MessageSentAction): State {
    return {
        ...state,
        messages: state.messages.map(message => {
            // swap out the temp message ID for the real one
            if (message.id === action.payload.tempId) {
                return {
                    ...message,
                    id: action.payload.realId,
                    timestamp: action.payload.timestamp,
                    hasSendError: false,
                    sending: false,
                };
            }
            return message;
        }),
    };
}

function messageSendError(state: State, action: MessageSendErrorAction): State {
    return {
        ...state,
        messages: state.messages.map(message => {
            if (message.id === action.payload.tempId) {
                return { ...message, hasSendError: true, sending: false };
            }
            return message;
        }),
    };
}

function messagesMarkedRead(state: State, { payload }: MessagesMarkedReadAction): State {
    return {
        ...state,
        messages: state.messages.map(message => {
            if (payload.messageIds.includes(Number(message.id))) {
                return {
                    ...message,
                    readReceipts: [
                        ...(message.readReceipts || []),
                        {
                            timestamp: new Date().toISOString(),
                            userId: payload.fromType === 'agent' ? payload.fromId : null,
                            visitorId: payload.fromType === 'visitor' ? payload.fromId : null,
                        },
                    ],
                };
            }
            return message;
        }),
    };
}

function removeConversation(state: State, action: RemoveConversationAction): State {
    if (action.payload.hide) {
        return {
            ...state,
            messages: [],
        };
    }

    return state;
}

export default createReducer(initialState, handlers);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/reducers/message.ts